import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { savePageHistory } from '../../redux/actions/action-creator';

/**
 * A hook that works like useState but persists its value in Redux history.
 * @param {string} key - Unique key for the state within the page.
 * @param {any} initialState - Default value.
 * @param {Object} [options] - Additional options.
 * @param {boolean} [options.ignoreSavedState=false] - If true, ignores the saved state on mount and uses initialState.
 */
export const usePageState = (key, initialState, options = {}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const path = location.pathname;

    const savedState = useSelector(state =>
        state.common.page_history?.[path]?.[key]
    );

    const { ignoreSavedState = false } = options;

    const [value, setValue] = useState(
        (savedState !== undefined && !ignoreSavedState) ? savedState : initialState
    );

    const isFirstMount = useRef(true);

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            if (ignoreSavedState) return;
        }

        if (!ignoreSavedState && savedState !== undefined && savedState !== value) {
            setValue(savedState);
        }
    }, [savedState, ignoreSavedState]);

    useEffect(() => {
        const syncToRedux = (dispatch, getState) => {
            const currentPageState = getState().common.page_history?.[path] || {};
            if (currentPageState[key] !== value) {
                dispatch(savePageHistory(path, { ...currentPageState, [key]: value }));
            }
        };
        dispatch(syncToRedux);
    }, [value, path, key, dispatch]);

    return [value, setValue];
};

export default usePageState;
